import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Gavel, Clock, Users, Eye, ChevronLeft, ChevronRight, 
  AlertTriangle, Trophy, Zap, Timer, Lock, Check, X,
  TrendingUp, History, DollarSign, Shield, Bell, Play, Share2, Copy
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';
import StatusSticker from '../components/StatusSticker';
import { formatIST, formatTimeIST } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuctionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasEntered, setHasEntered] = useState(false);
  const [hasRequiredPass, setHasRequiredPass] = useState(false);
  const [allowFreeMembers, setAllowFreeMembers] = useState(false);
  const [requiredPassTier, setRequiredPassTier] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [entering, setEntering] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isActivated, setIsActivated] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Fetch auction data
  const fetchAuction = useCallback(async () => {
    try {
      const response = await api.get(`/auction/${id}`);
      setAuction(response.data);
      
      // Set minimum bid amount
      const minBid = (response.data.current_bid || response.data.starting_bid) + response.data.bid_increment;
      setBidAmount(minBid.toString());
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Auction not found');
        navigate('/auctions');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Check user entry status
  const checkEntry = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get(`/auction/${id}/check-entry`);
      setHasEntered(response.data.has_entered);
      setHasRequiredPass(response.data.has_required_pass);
      setAllowFreeMembers(response.data.allow_free_members || false);
      setRequiredPassTier(response.data.required_pass_tier);
    } catch (error) {
      console.error('Failed to check entry:', error);
    }
  }, [id, user]);

  // Check if user is activated
  const checkActivation = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get('/auth/me');
      setIsActivated(response.data?.is_activated || false);
    } catch (error) {
      setIsActivated(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAuction();
    checkEntry();
    checkActivation();
  }, [fetchAuction, checkEntry, checkActivation]);

  // Timer countdown
  useEffect(() => {
    if (!auction?.end_time) return;

    const updateTimer = () => {
      const end = new Date(auction.end_time);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining({ total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeRemaining({
        total: diff,
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [auction?.end_time]);

  // WebSocket connection for live updates
  useEffect(() => {
    if (!auction || auction.status !== 'live') return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/auction/ws/${id}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_bid') {
          // Update auction with new bid
          setAuction(prev => ({
            ...prev,
            current_bid: data.bid.amount,
            total_bids: (prev?.total_bids || 0) + 1,
            recent_bids: [
              { ...data.bid, id: Date.now().toString() },
              ...(prev?.recent_bids || []).slice(0, 19)
            ]
          }));

          // Update end time if extended (anti-snipe)
          if (data.new_end_time) {
            setAuction(prev => ({ ...prev, end_time: data.new_end_time }));
            toast.info('Time extended due to last-minute bid!', { icon: '⏰' });
          }

          // Update minimum bid
          const minBid = data.bid.amount + (auction?.bid_increment || 100);
          setBidAmount(minBid.toString());

          toast.success(`New bid: ₹${data.bid.amount.toLocaleString()} by ${data.bid.bidder}`);
        }
      } catch (e) {
        console.error('WebSocket message error:', e);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [auction?.status, id]);

  const handleEnterAuction = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setEntering(true);
    try {
      await api.post(`/auction/${id}/enter`);
      toast.success('Successfully entered auction!');
      setHasEntered(true);
      setShowTermsModal(false);
      checkEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter auction');
    } finally {
      setEntering(false);
    }
  };

  const handlePlaceBid = async () => {
    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid bid amount');
      return;
    }

    const minBid = (auction?.current_bid || auction?.starting_bid) + auction?.bid_increment;
    if (amount < minBid) {
      toast.error(`Minimum bid is ₹${minBid.toLocaleString()}`);
      return;
    }

    setBidding(true);
    try {
      const response = await api.post(`/auction/${id}/bid?amount=${amount}`);
      toast.success(response.data.message);
      
      if (response.data.is_winner) {
        // User won with Buy Now
        fetchAuction();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setBidding(false);
    }
  };

  const handleBuyNow = async () => {
    if (!auction?.buy_now_price) return;
    
    if (!confirm(`Are you sure you want to Buy Now for ₹${auction.buy_now_price.toLocaleString()}?`)) {
      return;
    }

    setBidding(true);
    try {
      const response = await api.post(`/auction/${id}/bid?amount=${auction.buy_now_price}`);
      toast.success('Congratulations! You won the auction!');
      fetchAuction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to buy now');
    } finally {
      setBidding(false);
    }
  };

  const incrementBid = (amount) => {
    setBidAmount(prev => (parseFloat(prev) + amount).toString());
  };

  // Share function with OG image
  const handleShare = async () => {
    // Use share API endpoint for proper OG tags on social media
    const shareUrl = `${BACKEND_URL}/api/share/auction/${id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: auction?.title,
          text: `Auction: ${auction?.title} - Current Bid: ₹${auction?.current_bid || auction?.starting_bid}`,
          url: shareUrl
        });
      } catch (err) {
        copyLink(shareUrl);
      }
    } else {
      copyLink(shareUrl);
    }
  };
  
  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };
  
  // Get OG image for social sharing
  const getOgImage = () => {
    if (auction?.images && auction.images.length > 0) {
      const img = auction.images[0];
      if (img.startsWith('http')) return img;
      return `${BACKEND_URL}${img}`;
    }
    return `${BACKEND_URL}/static/og-default.png`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Gavel className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Auction Not Found</h2>
          <Link to="/auctions" className="text-primary hover:underline">Back to Auction House</Link>
        </div>
      </div>
    );
  }

  const isLive = auction.status === 'live';
  const isEnded = auction.status === 'ended' || (timeRemaining && timeRemaining.total <= 0);
  const isUpcoming = auction.status === 'upcoming';
  const minBid = (auction.current_bid || auction.starting_bid) + auction.bid_increment;

  // Non-activated users see lock screen
  if (!isActivated && user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <Lock className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-2">Activation Required</h2>
          <p className="text-muted-foreground mb-6">
            Activate your account to participate in auctions.
          </p>
          <Link
            to="/activation-packs"
            className="block w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold"
          >
            Activate Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* OG Meta Tags for Social Sharing */}
      {auction && (
        <Helmet>
          <title>{auction.title} | Live Auction - ANANT</title>
          <meta name="description" content={auction.description?.substring(0, 160)} />
          
          {/* Open Graph / Facebook / WhatsApp */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={`${window.location.origin}/auction/${id}`} />
          <meta property="og:title" content={`${auction.title} - Live Auction`} />
          <meta property="og:description" content={`Current Bid: ₹${(auction.current_bid || auction.starting_bid).toLocaleString()} | ${auction.description?.substring(0, 100)}`} />
          <meta property="og:image" content={getOgImage()} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={auction.title} />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${auction.title} - Live Auction`} />
          <meta name="twitter:description" content={`Current Bid: ₹${(auction.current_bid || auction.starting_bid).toLocaleString()}`} />
          <meta name="twitter:image" content={getOgImage()} />
        </Helmet>
      )}
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-white/10 bg-card/50 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/auctions" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-5 h-5" />
              Back to Auction House
            </Link>
            
            {/* Share Button */}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 transition-all"
            >
              {linkCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Share'}
            </button>
          </div>
        </div>
        
        {/* Live Indicator - moved below header */}
        {isLive && (
          <div className="max-w-6xl mx-auto px-4 pt-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-500 font-semibold">LIVE NOW</span>
            </div>
          </div>
        )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="glass-card overflow-hidden">
              <div className="relative aspect-video bg-black">
                {auction.images?.length > 0 ? (
                  <>
                    <img
                      src={getImageUrl(auction.images[currentImageIndex])}
                      alt={auction.title}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Status Sticker on Image */}
                    {auction.status_sticker && (
                      <StatusSticker 
                        type={auction.status_sticker}
                        customText={auction.sticker_text}
                        customColor={auction.sticker_color}
                        position="top-right"
                        size="lg"
                        animated={true}
                      />
                    )}
                    
                    {auction.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(prev => (prev - 1 + auction.images.length) % auction.images.length)}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(prev => (prev + 1) % auction.images.length)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center relative">
                    <Gavel className="w-20 h-20 text-muted-foreground" />
                    
                    {/* Status Sticker even when no images */}
                    {auction.status_sticker && (
                      <StatusSticker 
                        type={auction.status_sticker}
                        customText={auction.sticker_text}
                        customColor={auction.sticker_color}
                        position="top-right"
                        size="lg"
                        animated={true}
                      />
                    )}
                  </div>
                )}

                {/* Auction Status Badge */}
                <div className={`absolute top-4 left-4 px-4 py-2 rounded-full font-semibold ${
                  isLive ? 'bg-green-500 text-white' :
                  isEnded ? 'bg-gray-500 text-white' :
                  'bg-blue-500 text-white'
                }`}>
                  {isLive ? '● LIVE' : isEnded ? 'ENDED' : 'UPCOMING'}
                </div>
                
                {/* Video Indicator */}
                {auction.video_url && (
                  <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 rounded-full flex items-center gap-1 text-sm">
                    <Play className="w-4 h-4 text-red-500" />
                    <span>Video Available</span>
                  </div>
                )}
              </div>
            </div>

            {/* Video Section */}
            {auction.video_url && (
              <div className="glass-card p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                  Product Video
                </h3>
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  {auction.video_url.includes('youtube.com') || auction.video_url.includes('youtu.be') ? (
                    <iframe
                      src={auction.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title="Product Video"
                    />
                  ) : (
                    <video
                      src={auction.video_url}
                      controls
                      className="w-full h-full"
                    />
                  )}
                </div>
              </div>
            )}
            
            {/* Payment Status (if auction ended) */}
            {isEnded && (auction.token_amount || auction.part_payment_amount || auction.total_paid > 0) && (
              <div className="glass-card p-6 border-l-4 border-blue-500">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-500" />
                  Payment Status
                </h3>
                <div className="space-y-2 text-sm">
                  {auction.token_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Token Amount Received:</span>
                      <span className="font-semibold text-green-500">₹{auction.token_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {auction.part_payment_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Part Payment Done:</span>
                      <span className="font-semibold text-purple-500">₹{auction.part_payment_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {auction.total_paid > 0 && (
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="text-muted-foreground">Total Paid:</span>
                      <span className="font-bold text-lg">₹{auction.total_paid?.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timer */}
            {isLive && timeRemaining && (
              <div className={`glass-card p-6 ${timeRemaining.total < 60000 ? 'border-red-500 animate-pulse' : 'border-yellow-500/50'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <Timer className={`w-6 h-6 ${timeRemaining.total < 60000 ? 'text-red-500' : 'text-yellow-500'}`} />
                  <span className="font-semibold text-lg">
                    {timeRemaining.total < 60000 ? '⚠️ Ending Soon!' : 'Time Remaining'}
                  </span>
                  {timeRemaining.total < 30000 && (
                    <span className="text-xs bg-red-500 px-2 py-1 rounded-full">Anti-Snipe Active</span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Days', value: timeRemaining.days },
                    { label: 'Hours', value: timeRemaining.hours },
                    { label: 'Mins', value: timeRemaining.minutes },
                    { label: 'Secs', value: timeRemaining.seconds }
                  ].map(({ label, value }) => (
                    <div key={label} className={`text-center p-4 rounded-xl ${
                      timeRemaining.total < 60000 ? 'bg-red-500/20' : 'bg-white/5'
                    }`}>
                      <div className={`text-3xl font-bold font-mono ${
                        timeRemaining.total < 60000 ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                        {String(value).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-muted-foreground">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="glass-card p-6">
              <h1 className="text-2xl font-bold mb-4">{auction.title}</h1>
              <p className="text-muted-foreground whitespace-pre-wrap mb-6">{auction.description}</p>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {auction.views} views
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {auction.unique_bidders} bidders
                </span>
                <span className="flex items-center gap-1">
                  <Gavel className="w-4 h-4" />
                  {auction.total_bids} bids
                </span>
              </div>
            </div>

            {/* Bid History */}
            <div className="glass-card p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Recent Bids
              </h3>
              {auction.recent_bids?.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {auction.recent_bids.map((bid, idx) => (
                    <div 
                      key={bid.id || idx}
                      className={`flex items-center justify-between p-3 rounded-xl ${
                        idx === 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {idx === 0 && <Trophy className="w-5 h-5 text-yellow-500" />}
                        <div>
                          <p className="font-medium">{bid.bidder_name || bid.bidder || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatTimeIST(bid.timestamp)} IST
                          </p>
                        </div>
                      </div>
                      <p className={`font-bold ${idx === 0 ? 'text-green-500' : ''}`}>
                        ₹{bid.amount?.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No bids yet. Be the first!</p>
              )}
            </div>
          </div>

          {/* Right Column - Bidding Panel */}
          <div className="space-y-6">
            {/* Current Bid Card */}
            <div className="glass-card p-6">
              <div className="text-center mb-6">
                <p className="text-sm text-muted-foreground mb-1">Current Bid</p>
                <p className="text-4xl font-bold text-primary">
                  ₹{(auction.current_bid || auction.starting_bid)?.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Starting bid: ₹{auction.starting_bid?.toLocaleString()}
                </p>
              </div>

              {/* Buy Now Price */}
              {auction.buy_now_price && isLive && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground">Buy Now Price</p>
                  <p className="text-2xl font-bold text-green-500">
                    ₹{auction.buy_now_price?.toLocaleString()}
                  </p>
                </div>
              )}

              {/* Entry Required */}
              {!user ? (
                <div className="p-6 bg-white/5 rounded-xl text-center">
                  <Lock className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">Login to participate in this auction</p>
                  <Link
                    to="/login"
                    className="block w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold"
                  >
                    Login to Bid
                  </Link>
                </div>
              ) : !hasEntered ? (
                <div className="p-6 bg-white/5 rounded-xl text-center">
                  <Zap className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
                  <h3 className="font-semibold mb-2">Entry Required</h3>
                  {allowFreeMembers ? (
                    <>
                      <p className="text-sm text-green-400 mb-4">
                        <Check className="w-4 h-4 inline mr-1" />
                        This auction is <strong>FREE</strong> for all members!
                      </p>
                      <button
                        onClick={() => setShowTermsModal(true)}
                        className="w-full py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600"
                      >
                        Enter Auction (Free)
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">
                        You need a <strong>{requiredPassTier}</strong> pass to enter this auction.
                      </p>
                      {hasRequiredPass ? (
                        <button
                          onClick={() => setShowTermsModal(true)}
                          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold"
                        >
                          Enter Auction
                        </button>
                      ) : (
                        <Link
                          to="/deals/buy-pass"
                          className="block w-full py-3 bg-yellow-500 text-black rounded-xl font-semibold"
                        >
                          Buy {requiredPassTier} Pass
                        </Link>
                      )}
                    </>
                  )}
                </div>
              ) : isEnded ? (
                <div className="p-6 bg-white/5 rounded-xl text-center">
                  <Trophy className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
                  <h3 className="font-semibold mb-2">Auction Ended</h3>
                  {auction.winner_id === user?.id ? (
                    <p className="text-green-500">Congratulations! You won this auction!</p>
                  ) : auction.winner_id ? (
                    <p className="text-muted-foreground">
                      Final bid: ₹{auction.winner_bid?.toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-muted-foreground">No winner - reserve not met</p>
                  )}
                </div>
              ) : isUpcoming ? (
                <div className="p-6 bg-blue-500/10 rounded-xl text-center">
                  <Clock className="w-10 h-10 mx-auto mb-3 text-blue-500" />
                  <h3 className="font-semibold mb-2">Auction Starting Soon</h3>
                  <p className="text-sm text-muted-foreground">
                    Starts: {formatIST(auction.start_time)} IST
                  </p>
                </div>
              ) : (
                /* Bidding Panel */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Bid (₹)</label>
                    <input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      min={minBid}
                      step={auction.bid_increment}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-xl font-bold text-center"
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-center">
                      Minimum: ₹{minBid?.toLocaleString()} (increment: ₹{auction.bid_increment?.toLocaleString()})
                    </p>
                  </div>

                  {/* Quick Increment Buttons */}
                  <div className="grid grid-cols-3 gap-2">
                    {[auction.bid_increment, auction.bid_increment * 5, auction.bid_increment * 10].map(inc => (
                      <button
                        key={inc}
                        onClick={() => incrementBid(inc)}
                        className="py-2 bg-white/5 rounded-lg text-sm hover:bg-white/10"
                      >
                        +₹{inc?.toLocaleString()}
                      </button>
                    ))}
                  </div>

                  {/* Place Bid Button */}
                  <button
                    onClick={handlePlaceBid}
                    disabled={bidding || parseFloat(bidAmount) < minBid}
                    className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
                  >
                    {bidding ? (
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Gavel className="w-5 h-5" />
                        Place Bid
                      </>
                    )}
                  </button>

                  {/* Buy Now Button */}
                  {auction.buy_now_price && (
                    <button
                      onClick={handleBuyNow}
                      disabled={bidding}
                      className="w-full py-4 bg-green-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-green-600 disabled:opacity-50"
                    >
                      <Zap className="w-5 h-5" />
                      Buy Now - ₹{auction.buy_now_price?.toLocaleString()}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Auction Rules */}
            <div className="glass-card p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Auction Rules
              </h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  All bids are final and binding
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Anti-snipe: Bids in last 30 seconds add {auction.anti_snipe_minutes} minutes
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Winner must pay within 24 hours
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  Entry pass is consumed on auction entry
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Auction Terms & Conditions</h3>
              <button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-sm text-muted-foreground mb-6">
              <p><strong>1. Binding Bids:</strong> All bids placed are final and legally binding. You cannot cancel or retract a bid once placed.</p>
              
              <p><strong>2. Entry Fee:</strong> Entering this auction will consume one {requiredPassTier} pass from your inventory. This pass is non-refundable regardless of auction outcome.</p>
              
              <p><strong>3. Anti-Snipe Protection:</strong> If a bid is placed in the last 30 seconds, the auction will be extended by {auction?.anti_snipe_minutes || 2} minutes to allow fair competition.</p>
              
              <p><strong>4. Winner Obligations:</strong> If you win, you must complete payment within 24 hours. Failure to pay may result in account penalties and being banned from future auctions.</p>
              
              <p><strong>5. Buy Now:</strong> If available, the Buy Now option allows instant purchase at a fixed price, ending the auction immediately.</p>
              
              <p><strong>6. Reserve Price:</strong> Some auctions may have a hidden reserve price. If bidding doesn't meet the reserve, the item may not be sold.</p>
              
              <p><strong>7. Dispute Resolution:</strong> All disputes will be handled by ANANT WEB PORTAL administration. Their decision is final.</p>
            </div>

            <label className="flex items-start gap-3 p-4 bg-white/5 rounded-xl cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded"
              />
              <span className="text-sm">
                I have read and agree to the auction terms and conditions. I understand that my {requiredPassTier} pass will be consumed upon entry.
              </span>
            </label>

            <button
              onClick={handleEnterAuction}
              disabled={!termsAccepted || entering}
              className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {entering ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Gavel className="w-5 h-5" />
                  Enter Auction & Use Pass
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AuctionDetail;
